import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getUserAccessToken } from '../../../services/userSession';
import { getScreenHostOnboarding, patchScreenHostOnboarding } from '../../../services/screenHostOnboarding';
import posthog from '../../../services/posthog';
import { ScreenHostOnboardingContext } from './ScreenHostOnboardingContext';
import {
    ONBOARDING_SEQUENCE,
    ONBOARDING_STEPS,
    getNextOnboardingStepId,
} from './onboardingSteps';
import ScreenHostOnboardingChecklist from './ScreenHostOnboardingChecklist';
import ScreenHostOnboardingSpotlight from './ScreenHostOnboardingSpotlight';
import '../../Advertiser/AdvertiserOnboarding/AdvertiserOnboarding.css';
import './ScreenHostOnboarding.css';

function stepToTarget(stepId) {
    const meta = ONBOARDING_STEPS.find((s) => s.id === stepId);
    if (!meta) return null;
    return meta.navTarget || meta.pageTarget || null;
}

function stepToRoute(stepId) {
    const meta = ONBOARDING_STEPS.find((s) => s.id === stepId);
    return meta?.route || '/user/compose';
}

function InstallAppModal({ hardwareSetupPreference, tvAppLinks, onNext, onSkip }) {
    const isSmartTv = hardwareSetupPreference === 'smart_tv';
    const links = tvAppLinks || {};

    return (
        <div className="advertiser-onboarding-welcome-modal">
            <div className="advertiser-onboarding-welcome-card" style={{ maxWidth: 480 }}>
                <h2>{isSmartTv ? 'Download INVERT on your TV' : 'We will reach out soon'}</h2>
                {isSmartTv ? (
                    <>
                        <p>
                            Install the INVERT app on your Smart TV (Fire TV, Android TV, or Samsung), then sign in
                            with the email and password you just created.
                        </p>
                        <ul style={{ textAlign: 'left', margin: '12px 0', paddingLeft: 20, color: '#64748b' }}>
                            {links.fireTv && (
                                <li>
                                    <a href={links.fireTv} target="_blank" rel="noopener noreferrer">
                                        Fire TV / Amazon Appstore
                                    </a>
                                </li>
                            )}
                            {links.samsungTv && (
                                <li>
                                    <a href={links.samsungTv} target="_blank" rel="noopener noreferrer">
                                        Samsung TV
                                    </a>
                                </li>
                            )}
                            {links.androidTv && (
                                <li>
                                    <a href={links.androidTv} target="_blank" rel="noopener noreferrer">
                                        Android TV
                                    </a>
                                </li>
                            )}
                            {links.appleTv && (
                                <li>
                                    <a href={links.appleTv} target="_blank" rel="noopener noreferrer">
                                        Apple TV
                                    </a>
                                </li>
                            )}
                            {!links.fireTv && !links.samsungTv && !links.androidTv && !links.appleTv && (
                                <li>Search for <strong>INVERT</strong> in your TV&apos;s app store.</li>
                            )}
                        </ul>
                    </>
                ) : (
                    <p>
                        Our team will reach out within 1–2 business days to confirm your setup or ship your INVERT chip.
                        You can still explore your dashboard and create content with AI in the meantime.
                    </p>
                )}
                <div style={{ marginTop: 16 }}>
                    <button
                        type="button"
                        className="advertiser-onboarding-btn advertiser-onboarding-btn-primary"
                        onClick={onNext}
                    >
                        Got it
                    </button>
                    <button
                        type="button"
                        className="advertiser-onboarding-btn advertiser-onboarding-btn-ghost"
                        style={{ marginLeft: 8 }}
                        onClick={onSkip}
                    >
                        Skip tour
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function ScreenHostOnboardingProvider({ children, skipOnboarding = false }) {
    const token = getUserAccessToken();
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [tourActive, setTourActive] = useState(false);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [activeStepId, setActiveStepId] = useState(null);
    const [showWelcome, setShowWelcome] = useState(false);
    const [showInstallModal, setShowInstallModal] = useState(false);
    const [hardwareSetupPreference, setHardwareSetupPreference] = useState(null);
    const [tvAppLinks, setTvAppLinks] = useState({});

    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const forceOnboarding = searchParams.get('onboarding') === '1';

    const refreshOnboarding = useCallback(() => {
        if (!token || skipOnboarding) {
            setLoading(false);
            return Promise.resolve();
        }
        return getScreenHostOnboarding(token)
            .then((res) => {
                const data = res.data;
                if (data.hardwareSetupPreference) {
                    setHardwareSetupPreference(data.hardwareSetupPreference);
                }
                if (data.tvAppLinks) {
                    setTvAppLinks(data.tvAppLinks);
                }
                if (data.completed) {
                    setTourActive(false);
                    setCompletedSteps(data.steps || []);
                    setActiveStepId(null);
                    return;
                }
                const steps = data.steps || [];
                setCompletedSteps(steps);
                const shouldStart = data.showTour || forceOnboarding;
                if (shouldStart) {
                    setTourActive(true);
                    const next = getNextOnboardingStepId(steps);
                    setActiveStepId(next || 'welcome');
                    if (next === 'welcome' || (!next && forceOnboarding)) {
                        setShowWelcome(true);
                    }
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [token, forceOnboarding, skipOnboarding]);

    useEffect(() => {
        refreshOnboarding();
    }, [refreshOnboarding]);

    useEffect(() => {
        if (!tourActive || !activeStepId || activeStepId === 'welcome' || showWelcome || showInstallModal) {
            return;
        }
        if (activeStepId === 'install_app') {
            setShowInstallModal(true);
            return;
        }
        const route = stepToRoute(activeStepId);
        if (!location.pathname.startsWith(route.split('?')[0])) {
            navigate(route);
        }
    }, [activeStepId, tourActive, showWelcome, showInstallModal, location.pathname, navigate]);

    const markStep = useCallback(
        async (stepId) => {
            if (!token || !stepId) return;
            setCompletedSteps((prev) => (prev.includes(stepId) ? prev : [...prev, stepId]));
            try {
                await patchScreenHostOnboarding(token, { stepId });
                posthog.capture('screen host onboarding step completed', { step_id: stepId });
            } catch (e) {
                console.error(e);
            }
        },
        [token]
    );

    const skipTour = useCallback(async () => {
        if (!token) return;
        setTourActive(false);
        setActiveStepId(null);
        setShowWelcome(false);
        setShowInstallModal(false);
        try {
            await patchScreenHostOnboarding(token, { skip: true });
            posthog.capture('screen host onboarding skipped');
        } catch (e) {
            console.error(e);
        }
        if (forceOnboarding) {
            navigate('/user/compose', { replace: true });
        }
    }, [token, forceOnboarding, navigate]);

    const finishTour = useCallback(async () => {
        if (!token) return;
        setTourActive(false);
        setActiveStepId(null);
        setShowInstallModal(false);
        try {
            await patchScreenHostOnboarding(token, { complete: true });
            posthog.capture('screen host onboarding completed');
        } catch (e) {
            console.error(e);
        }
        if (forceOnboarding) {
            navigate('/user/compose', { replace: true });
        }
    }, [token, forceOnboarding, navigate]);

    const advanceFromStep = useCallback(
        async (currentId) => {
            await markStep(currentId);
            const merged = completedSteps.includes(currentId)
                ? completedSteps
                : [...completedSteps, currentId];
            const next = getNextOnboardingStepId(merged);
            if (!next || next === 'done') {
                await markStep('done');
                await finishTour();
                return;
            }
            setActiveStepId(next);
            if (next === 'welcome') {
                setShowWelcome(true);
            } else if (next === 'install_app') {
                setShowWelcome(false);
                setShowInstallModal(true);
            } else {
                setShowWelcome(false);
                setShowInstallModal(false);
            }
            navigate(stepToRoute(next));
        },
        [completedSteps, markStep, finishTour, navigate]
    );

    const goToNextStep = useCallback(async () => {
        if (!activeStepId) return;
        if (activeStepId === 'install_app') {
            setShowInstallModal(false);
        }
        await advanceFromStep(activeStepId);
    }, [activeStepId, advanceFromStep]);

    const startTour = useCallback(() => {
        setShowWelcome(false);
        setTourActive(true);
        const next = getNextOnboardingStepId(completedSteps) || 'compose_input';
        setActiveStepId(next);
        navigate(stepToRoute(next));
    }, [completedSteps, navigate]);

    const jumpToStep = useCallback(
        (stepId) => {
            setShowWelcome(false);
            setShowInstallModal(stepId === 'install_app');
            setTourActive(true);
            setActiveStepId(stepId);
            navigate(stepToRoute(stepId));
        },
        [navigate]
    );

    const replayTour = useCallback(() => {
        setShowInstallModal(false);
        setTourActive(true);
        setActiveStepId('welcome');
        setShowWelcome(true);
        setCompletedSteps([]);
        navigate('/user/compose');
        posthog.capture('screen host onboarding replayed');
    }, [navigate]);

    const contextValue = useMemo(
        () => ({
            loading,
            tourActive,
            activeStepId,
            completedSteps,
            showWelcome,
            hardwareSetupPreference,
            tvAppLinks,
            startTour,
            goToNextStep,
            skipTour,
            markStep,
            finishTour,
            jumpToStep,
            replayTour,
            isOnboardingStep: (id) => tourActive && activeStepId === id,
        }),
        [
            loading,
            tourActive,
            activeStepId,
            completedSteps,
            showWelcome,
            hardwareSetupPreference,
            tvAppLinks,
            startTour,
            goToNextStep,
            skipTour,
            markStep,
            finishTour,
            jumpToStep,
            replayTour,
        ]
    );

    const spotlightTarget = activeStepId ? stepToTarget(activeStepId) : null;

    return (
        <ScreenHostOnboardingContext.Provider value={contextValue}>
            {children}
            {!loading && !tourActive && (
                <button
                    type="button"
                    className="screen-host-onboarding-help-btn"
                    onClick={replayTour}
                    aria-label="Show dashboard tutorial"
                    title="Show tutorial"
                >
                    ?
                </button>
            )}
            {!loading && tourActive && (
                <>
                    {showWelcome && (
                        <div className="advertiser-onboarding-welcome-modal">
                            <div className="advertiser-onboarding-welcome-card">
                                <h2>Welcome to INVERT</h2>
                                <p>
                                    Your account is ready. Take a quick tour of your dashboard, then start creating
                                    content for your screens.
                                </p>
                                <button
                                    type="button"
                                    className="advertiser-onboarding-btn advertiser-onboarding-btn-primary"
                                    onClick={async () => {
                                        await markStep('welcome');
                                        startTour();
                                    }}
                                >
                                    Start tour
                                </button>
                                <button
                                    type="button"
                                    className="advertiser-onboarding-btn advertiser-onboarding-btn-ghost"
                                    style={{ marginLeft: 8 }}
                                    onClick={skipTour}
                                >
                                    Skip for now
                                </button>
                            </div>
                        </div>
                    )}
                    {showInstallModal && (
                        <InstallAppModal
                            hardwareSetupPreference={hardwareSetupPreference}
                            tvAppLinks={tvAppLinks}
                            onNext={goToNextStep}
                            onSkip={skipTour}
                        />
                    )}
                    {!showWelcome && !showInstallModal && (
                        <>
                            <ScreenHostOnboardingSpotlight
                                activeStepId={activeStepId}
                                targetSelector={spotlightTarget}
                                onNext={goToNextStep}
                                onSkip={skipTour}
                                showSkip
                            />
                            <ScreenHostOnboardingChecklist
                                completedSteps={completedSteps}
                                activeStepId={activeStepId}
                                onSkip={skipTour}
                                onJumpToStep={jumpToStep}
                            />
                        </>
                    )}
                </>
            )}
        </ScreenHostOnboardingContext.Provider>
    );
}
